import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Stack,
  Chip,
  Avatar,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AutoAwesome,
  Speed,
  TrendingUp,
  Security,
  CheckCircle,
  Star,
  ArrowForward,
  Email,
  Send,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function LandingPage() {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const features = [
    {
      icon: <AutoAwesome sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'AI-Powered Responses',
      description: 'Generate personalized, professional responses to customer reviews using advanced AI technology.'
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Automated Workflow',
      description: 'Set it and forget it. Our system monitors and responds to reviews 24/7 automatically.'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Boost Your Reputation',
      description: 'Improve customer satisfaction and online reputation with timely, thoughtful responses.'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with AWS infrastructure. Your data is safe and protected.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Restaurant Owner',
      avatar: 'SJ',
      rating: 5,
      text: 'ReplyMojo has transformed how we handle customer feedback. Our response rate went from 20% to 100%!'
    },
    {
      name: 'Tuba Atak',
      role: 'Retail Owner',
      avatar: 'TC',
      rating: 5,
      text: 'The AI responses are so natural, customers think I personally wrote each one. Amazing tool!'
    },
    {
      name: 'Lisa Rodriguez',
      role: 'Service Business',
      avatar: 'LR',
      rating: 5,
      text: 'Saved me hours every week. The custom prompts let me maintain my brand voice perfectly.'
    }
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');

    try {
      const response = await fetch('https://l1xj0vaiy0.execute-api.us-east-1.amazonaws.com/prod/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          subject: contactForm.company ? `Contact from ${contactForm.company}` : 'Contact Form Submission',
          message: contactForm.message
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', company: '', message: '' });
        
        setTimeout(() => {
          setContactOpen(false);
          setContactSuccess(false);
        }, 2000);
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setContactError('Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const handleContactChange = (field) => (event) => {
    setContactForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleScrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToFeatures = () => {
    const featuresSection = document.getElementById('features-section');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              ReplyMojo
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button color="primary" onClick={handleScrollToFeatures}>Features</Button>
            <Button color="primary" onClick={handleScrollToPricing}>Pricing</Button>
            <Button color="primary" onClick={() => setContactOpen(true)}>Contact</Button>
            <Button 
              variant="contained" 
              onClick={() => navigate('/app')}
              sx={{ ml: 2 }}
            >
              Sign In
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Chip 
              label="🚀 AI-Powered Review Management" 
              sx={{ mb: 3, bgcolor: 'primary.50', color: 'primary.main' }}
            />
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 3,
                color: 'primary.main'
              }}
            >
              Never Miss a Review Again
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
              Automatically respond to Google Business Profile reviews with AI-generated, 
              personalized responses that boost your reputation and customer satisfaction.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
              <Button 
                variant="contained" 
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/app')}
                sx={{ px: 4, py: 1.5 }}
              >
                Start Free Trial
              </Button>
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body2">No setup fees</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body2">Cancel anytime</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="body2">24/7 support</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box 
              sx={{ 
                bgcolor: 'white',
                borderRadius: 4,
                p: 4,
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                ⭐⭐⭐⭐⭐ Customer Review
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                "Amazing experience !! Love my bracelet"
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, borderLeft: 4, borderColor: 'primary.main' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  AI Generated Response:
                </Typography>
                <Typography variant="body1">
                  "Thank you so much for your wonderful feedback! We're absolutely thrilled to hear that you had an amazing experience and 
that you love your bracelet! It was our pleasure to create something special for you. We can't wait to see you again soon 
for your next piece. Enjoy wearing your beautiful new bracelet! ✨"
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }} id="features-section">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
            Why Choose ReplyMojo?
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Powerful features designed to boost your online reputation
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', p: 2, '&:hover': { boxShadow: 6 } }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Box id="pricing-section" sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Choose the plan that fits your business needs
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {/* Basic Plan */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  position: 'relative',
                  border: '2px solid',
                  borderColor: 'grey.200',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 4 }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Basic Plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Perfect for small businesses
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      $10
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per month
                    </Typography>
                  </Box>
                  <Stack spacing={2} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Up to 3 locations</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">100 AI responses/month</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Basic analytics</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Email support</Typography>
                    </Box>
                  </Stack>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    size="large"
                    onClick={() => navigate('/app')}
                  >
                    Start 30-Day Free Trial
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Plan */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  position: 'relative',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  boxShadow: 4
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    fontWeight: 'bold'
                  }}
                >
                  MOST POPULAR
                </Box>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Pro Plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Best for growing businesses
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      $30
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per month
                    </Typography>
                  </Box>
                  <Stack spacing={2} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Up to 10 locations</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">500 AI responses/month</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Advanced analytics</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Custom AI prompts</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Priority support</Typography>
                    </Box>
                  </Stack>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    size="large"
                    onClick={() => navigate('/app')}
                  >
                    Start 30-Day Free Trial
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Enterprise Plan */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  position: 'relative',
                  border: '2px solid',
                  borderColor: 'grey.200',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 4 }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Enterprise
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    For large organizations
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      Custom
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      pricing
                    </Typography>
                  </Box>
                  <Stack spacing={2} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Unlimited locations</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Unlimited AI responses</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">White-label solution</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">API access</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">Dedicated support</Typography>
                    </Box>
                  </Stack>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    size="large"
                    onClick={() => setContactOpen(true)}
                  >
                    Contact Sales
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* FAQ Section */}
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 4 }}>
              Frequently Asked Questions
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Is there a free trial?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes! All plans come with a 14-day free trial. No credit card required.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Can I change plans anytime?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Absolutely! Upgrade or downgrade your plan at any time from your dashboard.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    What happens if I exceed my limits?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We'll notify you when you're close to your limit and help you upgrade seamlessly.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Do you offer custom solutions?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes! Our Enterprise plan includes custom integrations and white-label options.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
              Loved by Business Owners
            </Typography>
            <Typography variant="h6" color="text.secondary">
              See what our customers are saying
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', p: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{testimonial.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Rating value={testimonial.rating} readOnly sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                      "{testimonial.text}"
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box 
          sx={{ 
            textAlign: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            p: 6,
            borderRadius: 4
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
            Ready to Transform Your Reviews?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join hundreds of businesses already using ReplyMojo to boost their reputation
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main',
              px: 4,
              py: 1.5,
              '&:hover': { bgcolor: 'grey.100' }
            }}
            onClick={() => navigate('/app')}
          >
            Start Your Free Trial Today
          </Button>
        </Box>
      </Container>

      {/* Contact Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
              Have Questions?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Our team is here to help you get started with ReplyMojo. 
              Reach out for demos, pricing, or technical support.
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ mr: 2, color: 'success.main' }} />
                <Typography>Response within 24 hours</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ mr: 2, color: 'success.main' }} />
                <Typography>Free consultation available</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 4 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                  Get in Touch
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<Send />}
                  onClick={() => setContactOpen(true)}
                  sx={{ mb: 2 }}
                >
                  Send us a Message
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Quick response guaranteed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Contact Modal */}
      <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">Contact Us</Typography>
          <Button onClick={() => setContactOpen(false)} sx={{ minWidth: 'auto', p: 1 }}>
            <Close />
          </Button>
        </DialogTitle>
        <form onSubmit={handleContactSubmit}>
          <DialogContent>
            {contactSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Message sent successfully! We'll get back to you soon.
              </Alert>
            )}
            {contactError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {contactError}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={contactForm.name}
                  onChange={handleContactChange('name')}
                  required
                  disabled={contactLoading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={handleContactChange('email')}
                  required
                  disabled={contactLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company (Optional)"
                  value={contactForm.company}
                  onChange={handleContactChange('company')}
                  disabled={contactLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={contactForm.message}
                  onChange={handleContactChange('message')}
                  required
                  disabled={contactLoading}
                  placeholder="Tell us about your business and how we can help..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setContactOpen(false)} 
              disabled={contactLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={contactLoading || !contactForm.name || !contactForm.email || !contactForm.message}
              startIcon={contactLoading ? <CircularProgress size={20} /> : <Send />}
            >
              {contactLoading ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoAwesome sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  ReplyMojo
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                AI-powered review management for modern businesses
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ textAlign: 'right', opacity: 0.8 }}>
                © 2026 ReplyMojo. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
